/* eslint-disable no-continue */
import {
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  BooleanKeyframeTrack,
  KeyframeTrack,
  LoopOnce,
  Material,
  Mesh,
  NumberKeyframeTrack,
  Object3D,
  QuaternionKeyframeTrack,
  VectorKeyframeTrack,
} from 'three';
import { Animation } from '../model/animation';
import { KeyframePosition, KeyframeRotation } from '../model/keyframe';

export function addAnimationToScene(
  scene: Object3D,
  animation: Animation
): { mixer: AnimationMixer; actions: AnimationAction[] } {
  const mixer = new AnimationMixer(scene);
  const actions: AnimationAction[] = [];

  for (const [name, channel] of Object.entries(animation.channels)) {
    const object = scene.getObjectByName(name);
    if (!object) {
      // eslint-disable-next-line no-console
      console.warn(`Cannot find object ${name}`);
      continue;
    }
    object.visible = !channel.hidden;
    const positionTimes: number[] = [];
    const positions: KeyframePosition[] = [];
    const rotationTimes: number[] = [];
    const rotations: KeyframeRotation[] = [];
    const opacityTimes: number[] = [];
    const opacities: number[] = [];
    for (const { time, position, rotation, opacity } of channel.keyframes) {
      if (position) {
        positionTimes.push(time);
        positions.push(position);
      }
      if (rotation) {
        rotationTimes.push(time);
        rotations.push(rotation);
      }
      if (opacity != null) {
        opacityTimes.push(time);
        opacities.push(opacity);
      }
    }
    const tracks: KeyframeTrack[] = [];
    if (positions.length) {
      const vectors = positions.flatMap((it) => it);
      const track = new VectorKeyframeTrack('.position', positionTimes, vectors);
      tracks.push(track);
    }
    if (rotations.length) {
      const quaternions = rotations.flatMap((it) => it);
      const track = new QuaternionKeyframeTrack('.quaternion', rotationTimes, quaternions);
      tracks.push(track);
    }
    if (opacities.length && object instanceof Mesh && object.material instanceof Material) {
      const opacityTrack = new NumberKeyframeTrack('.material.opacity', opacityTimes, opacities);
      const transparentTrack = new BooleanKeyframeTrack(
        '.material.transparent',
        opacityTimes,
        opacities.map(() => true)
      );
      tracks.push(opacityTrack, transparentTrack);
    }
    const clip = new AnimationClip(`${name}_Clip`, undefined, tracks);
    const action = mixer.clipAction(clip, object);
    action.setLoop(LoopOnce, 0);
    action.clampWhenFinished = true;
    action.play();
    actions.push(action);
  }

  return { mixer, actions };
}
